import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  AlignmentType,
} from "docx";

// Splits "**bold**", "*italic*"/"_italic_", and "`code`" spans out of a line of
// markdown text into docx TextRuns, keeping everything else as plain runs.
// `extra` options (e.g. { bold: true }) are merged into every run produced,
// so a caller can force a whole line bold (table headers) while still
// respecting inline emphasis underneath.
function parseInlineRuns(text, extra = {}) {
  const runs = [];
  const pattern = /(\*\*.+?\*\*|__.+?__|\*.+?\*|_.+?_|`.+?`)/g;
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      runs.push(new TextRun({ text: text.slice(lastIndex, match.index), ...extra }));
    }

    const token = match[0];
    if (token.startsWith("**") || token.startsWith("__")) {
      runs.push(new TextRun({ text: token.slice(2, -2), ...extra, bold: true }));
    } else if (token.startsWith("`")) {
      runs.push(new TextRun({ text: token.slice(1, -1), ...extra, font: "Consolas" }));
    } else {
      runs.push(new TextRun({ text: token.slice(1, -1), ...extra, italics: true }));
    }

    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) {
    runs.push(new TextRun({ text: text.slice(lastIndex), ...extra }));
  }

  return runs.length > 0 ? runs : [new TextRun({ text, ...extra })];
}

function isTableSeparatorRow(line) {
  return /^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?$/.test(line.trim());
}

function splitTableRow(line) {
  let row = line.trim();
  if (row.startsWith("|")) row = row.slice(1);
  if (row.endsWith("|")) row = row.slice(0, -1);
  return row.split("|").map((cell) => cell.trim());
}

const CELL_BORDER = { style: BorderStyle.SINGLE, size: 4, color: "999999" };
const TABLE_BORDERS = {
  top: CELL_BORDER,
  bottom: CELL_BORDER,
  left: CELL_BORDER,
  right: CELL_BORDER,
  insideHorizontal: CELL_BORDER,
  insideVertical: CELL_BORDER,
};

function buildTable(headerCells, bodyRows) {
  const colWidth = 100 / headerCells.length;

  const headerRow = new TableRow({
    tableHeader: true,
    children: headerCells.map(
      (text) =>
        new TableCell({
          width: { size: colWidth, type: WidthType.PERCENTAGE },
          shading: { fill: "F2F2F2" },
          children: [new Paragraph({ children: parseInlineRuns(text, { bold: true }) })],
        })
    ),
  });

  const rows = bodyRows.map(
    (cells) =>
      new TableRow({
        children: headerCells.map(
          (_, i) =>
            new TableCell({
              width: { size: colWidth, type: WidthType.PERCENTAGE },
              children: [new Paragraph({ children: parseInlineRuns(cells[i] || "") })],
            })
        ),
      })
  );

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: TABLE_BORDERS,
    rows: [headerRow, ...rows],
  });
}

// Converts a block of markdown text (headings, bullet/numbered lists, pipe
// tables, bold/italic emphasis, plain paragraphs) into docx content nodes.
function markdownToDocxNodes(markdown) {
  const lines = markdown.replace(/\r\n/g, "\n").split("\n");
  const nodes = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed === "") {
      i += 1;
      continue;
    }

    // Pipe-style table: a header row followed by a "---|---" separator row.
    if (trimmed.startsWith("|") && i + 1 < lines.length && isTableSeparatorRow(lines[i + 1])) {
      const headerCells = splitTableRow(trimmed);
      i += 2;
      const bodyRows = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        bodyRows.push(splitTableRow(lines[i]));
        i += 1;
      }
      nodes.push(buildTable(headerCells, bodyRows));
      nodes.push(new Paragraph({ text: "" }));
      continue;
    }

    const headingMatch = trimmed.match(/^(#{1,4})\s+(.*)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const headingLevels = [
        HeadingLevel.HEADING_1,
        HeadingLevel.HEADING_2,
        HeadingLevel.HEADING_3,
        HeadingLevel.HEADING_4,
      ];
      nodes.push(
        new Paragraph({
          heading: headingLevels[Math.min(level, headingLevels.length) - 1],
          children: parseInlineRuns(headingMatch[2]),
        })
      );
      i += 1;
      continue;
    }

    const bulletMatch = trimmed.match(/^[-*+]\s+(.*)$/);
    if (bulletMatch) {
      nodes.push(
        new Paragraph({
          bullet: { level: 0 },
          children: parseInlineRuns(bulletMatch[1]),
        })
      );
      i += 1;
      continue;
    }

    const numberedMatch = trimmed.match(/^\d+\.\s+(.*)$/);
    if (numberedMatch) {
      nodes.push(
        new Paragraph({
          numbering: { reference: "report-numbering", level: 0 },
          children: parseInlineRuns(numberedMatch[1]),
        })
      );
      i += 1;
      continue;
    }

    nodes.push(new Paragraph({ children: parseInlineRuns(trimmed) }));
    i += 1;
  }

  return nodes;
}

// Builds a .docx Blob for a "The Sea" conversation, preserving headings,
// bullets, emphasis, and turning any pipe-style markdown tables in agent
// answers into real Word tables.
export async function buildConversationDocx(messages) {
  const children = [
    new Paragraph({
      text: "askTheSea — Conversation Report",
      heading: HeadingLevel.TITLE,
    }),
    new Paragraph({
      children: [new TextRun({ text: `Generated ${new Date().toLocaleString()}`, italics: true, color: "666666" })],
    }),
    new Paragraph({ text: "" }),
  ];

  messages.forEach((m) => {
    if (m.role === "user") {
      children.push(
        new Paragraph({
          spacing: { before: 200 },
          children: [new TextRun({ text: "Q: ", bold: true }), ...parseInlineRuns(m.text)],
        })
      );
    } else if (m.role === "agent") {
      children.push(
        new Paragraph({
          spacing: { before: 120 },
          children: [new TextRun({ text: "A:", bold: true })],
        })
      );
      children.push(...markdownToDocxNodes(m.text || ""));

      if (m.sources && m.sources.length > 0) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Sources: ", bold: true, italics: true }),
              new TextRun({ text: m.sources.join(", "), italics: true }),
            ],
          })
        );
      }
      children.push(new Paragraph({ text: "" }));
    }
  });

  const doc = new Document({
    numbering: {
      config: [
        {
          reference: "report-numbering",
          levels: [
            {
              level: 0,
              format: "decimal",
              text: "%1.",
              alignment: AlignmentType.START,
            },
          ],
        },
      ],
    },
    sections: [{ children }],
  });

  return Packer.toBlob(doc);
}
